import html2canvas from "html2canvas";
import type { Attachment } from "ai";
import type { PageContextPayload } from "./page-context-types";

const CAPTURE_MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.82;

function scaleCanvas(source: HTMLCanvasElement, maxWidth: number): HTMLCanvasElement {
  if (source.width <= maxWidth) return source;
  const ratio = maxWidth / source.width;
  const scaled = document.createElement("canvas");
  scaled.width = maxWidth;
  scaled.height = Math.round(source.height * ratio);
  const ctx = scaled.getContext("2d");
  if (!ctx) return source;
  ctx.drawImage(source, 0, 0, scaled.width, scaled.height);
  return scaled;
}

/**
 * Snapshot the visible alert panel (or artifact fallback) for vision context.
 * Returns null on failure — submit should still proceed with text context only.
 */
export async function captureAlertPanelScreenshot(): Promise<Attachment | null> {
  if (typeof document === "undefined") return null;

  const target =
    document.querySelector<HTMLElement>("[data-alert-panel]") ??
    document.querySelector<HTMLElement>('[data-tour="artifact"]');

  if (!target) return null;

  try {
    console.info("[capturePageContext] screenshot capture started", {
      selector: target.hasAttribute("data-alert-panel")
        ? "data-alert-panel"
        : "data-tour=artifact",
      alertTab: target.getAttribute("data-alert-tab"),
      clientWidth: target.clientWidth,
      clientHeight: target.clientHeight,
      scrollWidth: target.scrollWidth,
      scrollHeight: target.scrollHeight,
    });
    const raw = await html2canvas(target, {
      backgroundColor: null,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      useCORS: true,
      logging: false,
      windowWidth: target.scrollWidth || target.clientWidth,
      windowHeight: target.scrollHeight || target.clientHeight,
    });

    const canvas = scaleCanvas(raw, CAPTURE_MAX_WIDTH);
    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);

    console.info("[capturePageContext] screenshot capture success", {
      width: canvas.width,
      height: canvas.height,
      contentType: "image/jpeg",
      approxBytes: Math.round((dataUrl.length * 3) / 4),
    });

    return {
      url: dataUrl,
      name: `page-context-${Date.now()}.jpg`,
      contentType: "image/jpeg",
    };
  } catch (err) {
    console.warn("[capturePageContext] screenshot failed:", err);
    return null;
  }
}

export function buildPageContextPayload(input: {
  isAlertsRoute: boolean;
  alertId?: string | null;
  alertName?: string | null;
  alertTab?: "dashboard" | "description" | "canvas";
  isOwner?: boolean;
  hasScreenshot?: boolean;
  route?: string;
}): PageContextPayload {
  const page_type = input.isAlertsRoute
    ? "alert_detail"
    : input.alertId
      ? "chat_artifact"
      : "chat_only";

  return {
    page_type,
    alert_id: input.alertId ?? undefined,
    alert_name: input.alertName ?? undefined,
    alert_tab: input.alertTab,
    is_owner: input.isOwner,
    has_screenshot: input.hasScreenshot,
    route:
      input.route ??
      (typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined),
  };
}
